import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, BookOpen, Notebook, Play, Mic, Newspaper, MessageCircle,
} from "lucide-react";
import BrandHeader from "@/components/BrandHeader";
import BrandFooter from "@/components/BrandFooter";
import EmailSignup from "@/components/EmailSignup";

const IMG = "https://images.unsplash.com/photo-1519791883288-dc8bd696e667?auto=format&fit=crop&w=1800&q=80";
const EASE = [0.16, 1, 0.3, 1];

const categories = [
  { Icon: BookOpen, title: "Free Guides", slug: "guides", body: "Practical, printable, and free — start today.", available: true },
  { Icon: Notebook, title: "Journals", slug: "journals", body: "7, 30, and 90-day discipline journals.", available: true },
  { Icon: Play, title: "Videos", slug: "videos", body: "Short lessons on habits, mindset, and health.", available: false },
  { Icon: Mic, title: "Podcast", slug: "podcast", body: "Real conversations on wellness.", available: false },
  { Icon: Newspaper, title: "Articles", slug: "articles", body: "Written encouragement, weekly.", available: false },
  { Icon: MessageCircle, title: "Community", slug: "community", body: "A quiet, kind space to grow together.", available: false },
];

export default function Resources() {
  return (
    <div className="min-h-screen bg-white text-[#333333]" data-testid="resources-page">
      <BrandHeader variant="cream" />
      <section className="relative bg-[#F8F6F2] pt-16 pb-20 md:pt-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">Resources</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-[4rem] font-medium tracking-tight text-[#333333] leading-[1.05]">
              Tools for the <span className="italic">quiet work</span> of getting better.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#4A4A4A] max-w-xl leading-relaxed">
              Free guides, printable journals, and thoughtful conversations — all designed to help you strengthen every area of wellness, one small choice at a time.
            </p>
          </div>
          <div className="lg:col-span-5 relative hidden lg:block">
            <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-30px_rgba(51,51,51,0.25)]">
              <img src={IMG} alt="Warm morning workspace" className="w-full h-[440px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {categories.map(({ Icon, title, slug, body, available }, i) => (
              <motion.div
                key={slug}
                initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.55, delay: (i % 3) * 0.05, ease: EASE }}
              >
                <Link
                  to={`/resources/${slug}`}
                  className="group block bg-[#F8F6F2] rounded-3xl p-8 border border-[#EDE9E1] hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full"
                  data-testid={`resource-card-${slug}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F6C453]/25 text-[#B8860B] group-hover:bg-[#F6C453] group-hover:text-white transition-colors">
                      <Icon className="h-5.5 w-5.5" strokeWidth={1.75} />
                    </span>
                    {!available && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#8FAF8A] bg-[#8FAF8A]/10 rounded-full px-2.5 py-1">Coming Soon</span>
                    )}
                  </div>
                  <h3 className="mt-6 font-display text-xl md:text-2xl font-bold text-[#333333]">{title}</h3>
                  <p className="mt-2 text-[#4A4A4A]">{body}</p>
                  <div className="mt-6 inline-flex items-center gap-2 text-[#333333] font-semibold text-sm">
                    Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="join" className="py-20 md:py-24 bg-[#F8F6F2]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">Newsletter</p>
          <h2 className="font-serif-display text-3xl md:text-4xl font-medium tracking-tight text-[#333333]">
            Get one thoughtful email each week.
          </h2>
          <p className="mt-4 text-[#4A4A4A]">Free guides land in your inbox as we publish them.</p>
          <div className="mt-8 max-w-2xl mx-auto text-left">
            <EmailSignup source="resources" testIdPrefix="join" overline={null} headline={null} subtext={null} compact />
          </div>
        </div>
      </section>

      <BrandFooter />
    </div>
  );
}
